import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import SingleSelect from '../../../components/atoms/forms/SingleSelect';
import ReasonPicker from './ReasonPicker';
import { REMOVE_REASONS, isReasonComplete } from './reasons';

// Bulk remove. The supervisor pre-selected stops on the route card; this
// modal captures a reason per selected stop. The "Bulk apply" dropdown sets
// the same reason on every selected stop in one click — useful when an
// entire chunk of the route is being cut for the same cause.

const StyledDiv = styledComponent('div', {
  '& .rsm-bulk': {
    background: theme.colors.bgMuted,
    padding: theme.spacing.bodyPadding,
    borderRadius: theme.radii.input,
    marginBottom: theme.spacing.fieldGap,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
    flexWrap: 'wrap',
  },
  '& .rsm-bulk-label': {
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
  },
  // Vertical stack — each row is a small card with the name on top, the
  // reason dropdown below, and (when a reason is set) a note input below
  // that. Stacking gives the dropdown the full modal width so long location
  // ids don't squeeze it into a sliver.
  '& .rsm-row': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.gapMd,
    padding: `${theme.spacing.gapXl} 0`,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },
  '& .rsm-row:last-of-type': {
    borderBottom: 'none',
  },
  '& .rsm-row-name': {
    fontSize: theme.typography.body.fontSize,
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.medium,
    wordBreak: 'break-all',
  },
  '& .rsm-row-index': {
    color: theme.colors.textMuted,
    fontWeight: theme.fontWeights.normal,
    marginRight: theme.spacing.inputLabelGap,
  },
});

function RemoveStopsModal({
  show,
  selectedStops, // [{ stop, stopKey }]
  initialReasons,
  onSubmit,
  onCancel,
}) {
  const [reasons, setReasons] = useState({}); // stopKey -> { value, note }

  // Reset only on the `false → true` edge — without this, any parent
  // re-render that passes a new inline `selectedStops` or `initialReasons`
  // reference while `show` stays true would re-seed (clobbering reasons
  // the supervisor has already captured). Mirrors the re-seed guard in
  // PinStopModal.
  const prevShowRef = useRef(false);
  useEffect(() => {
    if (show && !prevShowRef.current) {
      const seed = {};
      selectedStops.forEach(({ stopKey }) => {
        seed[stopKey] = (initialReasons && initialReasons[stopKey]) || {
          value: '',
          note: '',
        };
      });
      setReasons(seed);
    }
    prevShowRef.current = show;
  }, [show, selectedStops, initialReasons]);

  const setRowReason = (stopKey, partial) => {
    setReasons((prev) => ({
      ...prev,
      [stopKey]: { ...prev[stopKey], ...partial },
    }));
  };

  const applyBulkReason = (value) => {
    if (!value) return;
    setReasons((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        // Preserve any existing note when bulk-applying — supervisors who
        // already wrote a detail for one row don't lose it.
        next[k] = { value, note: prev[k]?.note || '' };
      });
      return next;
    });
  };

  const allComplete =
    selectedStops.length > 0 &&
    selectedStops.every(({ stopKey }) => isReasonComplete(reasons[stopKey]));

  const handleSubmit = () => {
    if (!allComplete) return;
    onSubmit(reasons);
  };

  const isSingle = selectedStops.length === 1;
  const title = isSingle
    ? 'Remove stop'
    : `Remove ${selectedStops.length} stops`;
  const submitLabel = isSingle ? 'Confirm removal' : 'Confirm removals';

  return (
    <Modal show={show} onHide={onCancel} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledDiv>
          {/* Bulk apply is only useful when multiple stops share a reason —
              hide it for the single-stop case so the modal stays focused. */}
          {!isSingle && (
            <div className="rsm-bulk">
              <span className="rsm-bulk-label">Bulk apply reason:</span>
              <div
                style={{ flex: 1, minWidth: theme.sizes.filterFieldMinWidth }}
              >
                <SingleSelect
                  options={REMOVE_REASONS}
                  value={null}
                  onChange={(opt) => applyBulkReason(opt ? opt.value : '')}
                  placeholder="— Select a reason for all —"
                  isClearable
                  data-testid="remove-stops-bulk"
                />
              </div>
            </div>
          )}

          {selectedStops.map(({ stop, stopKey }, position) => {
            const entry = reasons[stopKey] || { value: '', note: '' };
            const stopLabel =
              stop.displayName || stop.id || stop.stop || 'Unnamed';
            return (
              <div
                key={stopKey}
                className="rsm-row"
                data-testid={`remove-stops-row-${stopKey}`}
              >
                <div className="rsm-row-name">
                  {!isSingle && (
                    <span className="rsm-row-index">
                      {`Stop ${position + 1} —`}
                    </span>
                  )}
                  {stopLabel}
                </div>
                <ReasonPicker
                  options={REMOVE_REASONS}
                  value={entry.value}
                  note={entry.note}
                  onChange={(next) => setRowReason(stopKey, next)}
                  reasonTestId={`remove-stops-reason-${stopKey}`}
                  noteTestId={`remove-stops-note-${stopKey}`}
                />
              </div>
            );
          })}
        </StyledDiv>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel} data-testid="remove-stops-cancel">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!allComplete}
          className="btn-success"
          data-testid="remove-stops-submit"
        >
          {submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

RemoveStopsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  selectedStops: PropTypes.arrayOf(
    PropTypes.shape({
      stopKey: PropTypes.string.isRequired,
      stop: PropTypes.shape({
        id: PropTypes.string,
        stop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        displayName: PropTypes.string,
      }).isRequired,
    }),
  ).isRequired,
  initialReasons: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

RemoveStopsModal.defaultProps = {
  initialReasons: null,
};

export default RemoveStopsModal;
